'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import ReactSelect, { components } from 'react-select';
import { ChevronDown, X, Check } from 'lucide-react';
import { cn } from '../utils/cn';
// Custom components for icons
const DropdownIndicator = (props) => (_jsx(components.DropdownIndicator, { ...props, children: _jsx(ChevronDown, { className: "h-4 w-4 text-gray-400 dark:text-gray-500" }) }));
const ClearIndicator = (props) => (_jsx(components.ClearIndicator, { ...props, children: _jsx(X, { className: "h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" }) }));
const MultiValueRemove = (props) => (_jsx(components.MultiValueRemove, { ...props, children: _jsx(X, { className: "h-3 w-3" }) }));
// Once a multi-select's chips wrap past `maxRows`, hides the overflow chips
// and shows a single "+N more" pill instead — measured from the actual
// rendered chip positions (offsetTop) so it adapts to however wide each
// chip's label happens to be, not a fixed chip-count guess.
function createRowLimitedValueContainer(maxRows) {
    return function RowLimitedValueContainer(props) {
        const wrapRef = React.useRef(null);
        const [hiddenFrom, setHiddenFrom] = React.useState(null);
        const total = props.getValue().length;
        React.useLayoutEffect(() => {
            const wrap = wrapRef.current;
            if (!wrap || total === 0) {
                setHiddenFrom(null);
                return;
            }
            const chips = Array.from(wrap.querySelectorAll('.erp-select__multi-value'));
            chips.forEach(el => { el.style.display = ''; });
            if (chips.length === 0) {
                setHiddenFrom(null);
                return;
            }
            let rowTop = null;
            let rowCount = 0;
            let cutoff = null;
            chips.forEach((el, i) => {
                if (rowTop === null || Math.abs(el.offsetTop - rowTop) > 2) {
                    rowCount++;
                    rowTop = el.offsetTop;
                }
                if (rowCount > maxRows && cutoff === null) {
                    cutoff = i;
                }
            });
            chips.forEach((el, i) => {
                if (cutoff !== null && i >= cutoff)
                    el.style.display = 'none';
            });
            setHiddenFrom(cutoff);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [total, JSON.stringify(props.getValue().map((v) => v.value))]);
        return (_jsxs(components.ValueContainer, { ...props, children: [_jsx("div", { ref: wrapRef, style: { display: 'contents' }, children: props.children }), hiddenFrom !== null && (_jsxs("div", { className: "mx-1 my-0.5 rounded px-2 py-0.5 text-[13px] font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 whitespace-nowrap", children: ["+", total - hiddenFrom, " more"] }))] }));
    };
}
// Custom Option component with checkbox for multi-select
const CheckboxOption = (props) => {
    const { isSelected, isMulti, children } = props;
    if (!isMulti) {
        return _jsx(components.Option, { ...props, children: children });
    }
    return (_jsx(components.Option, { ...props, children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: cn("flex h-4 w-4 items-center justify-center rounded border transition-colors", isSelected
                        ? "bg-primary-600 border-primary-600"
                        : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"), children: isSelected && _jsx(Check, { className: "h-3 w-3 text-white" }) }), _jsx("span", { children: children })] }) }));
};
export function Select({ label, error, helperText, required, className, darkMode, onChange, maxVisibleRows, ...props }) {
    // Auto-detect dark mode from document if not explicitly set
    const [isDarkMode, setIsDarkMode] = React.useState(darkMode ?? (typeof window !== 'undefined' && document.documentElement.classList.contains('dark')));
    // Listen for dark mode changes
    React.useEffect(() => {
        if (darkMode !== undefined) {
            setIsDarkMode(darkMode);
            return;
        }
        if (typeof window === 'undefined')
            return;
        const updateDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        // Initial check
        updateDarkMode();
        // Watch for class changes on documentElement
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    updateDarkMode();
                }
            });
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
        return () => observer.disconnect();
    }, [darkMode]);
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            // Match input field background - use gray-50/gray-700 when disabled to match text input disabled state
            backgroundColor: state.isDisabled
                ? (isDarkMode ? '#374151' : '#f9fafb') // gray-700/gray-50 for disabled
                : (isDarkMode ? '#1f2937' : '#ffffff'), // gray-800/white for enabled
            borderColor: error
                ? '#ef4444'
                : state.isFocused
                    ? '#3b82f6'
                    : (isDarkMode ? '#4b5563' : '#d1d5db'),
            borderWidth: '1px',
            borderRadius: '6px',
            boxShadow: state.isFocused ? '0 0 0 2px #3b82f640' : 'none',
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            opacity: state.isDisabled ? 1 : 1, // Keep full opacity, rely on background color for disabled indication
            '&:hover': {
                borderColor: state.isDisabled
                    ? (isDarkMode ? '#4b5563' : '#d1d5db') // No hover effect when disabled
                    : error
                        ? '#ef4444'
                        : (isDarkMode ? '#6b7280' : '#9ca3af'),
            },
            // minHeight only (no maxHeight/height) so a multi-select with many
            // chips can grow and wrap onto multiple rows instead of clipping them.
            minHeight: '40px',
            fontSize: '13px', // Match input font size
            color: 'inherit', // Inherit text color from parent
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '4px 12px',
            minHeight: '40px',
            fontSize: '13px',
        }),
        input: (provided) => ({
            ...provided,
            color: 'inherit', // Inherit from theme
            margin: '0',
            padding: '0',
            fontSize: '13px',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            fontSize: '13px',
            // react-select leaves the placeholder wrappable, so a long one ("All Departments")
            // breaks onto a second line in a narrow column and makes the control taller than
            // its neighbours. Clip it instead — the full text is in the menu anyway.
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'inherit', // Inherit from theme
            fontSize: '13px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            zIndex: 9999,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? (isDarkMode ? '#3b82f6' : '#3b82f6')
                : state.isFocused
                    ? (isDarkMode ? '#374151' : '#f3f4f6')
                    : 'transparent',
            color: state.isSelected
                ? '#ffffff'
                : (isDarkMode ? '#ffffff' : '#111827'),
            fontSize: '13px',
            borderRadius: '4px',
            margin: '2px 0',
            padding: '8px 12px',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: state.isSelected
                    ? (isDarkMode ? '#3b82f6' : '#3b82f6')
                    : (isDarkMode ? '#374151' : '#f3f4f6'),
            },
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#374151' : '#e5e7eb',
            borderRadius: '4px',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: isDarkMode ? '#ffffff' : '#111827',
            fontSize: '13px',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            '&:hover': {
                backgroundColor: isDarkMode ? '#ef4444' : '#ef4444',
                color: '#ffffff',
            },
            borderRadius: '0 4px 4px 0',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            minHeight: '40px',
            alignSelf: 'stretch',
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#4b5563' : '#d1d5db',
            marginTop: '8px',
            marginBottom: '8px',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            padding: '0 8px',
            '&:hover': {
                color: isDarkMode ? '#ffffff' : '#111827',
            },
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            padding: '0 8px',
            '&:hover': {
                color: isDarkMode ? '#ffffff' : '#111827',
            },
        }),
        noOptionsMessage: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            padding: '12px',
        }),
        loadingMessage: (provided) => ({
            ...provided,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            padding: '12px',
        }),
    };
    const handleChange = (newValue) => {
        if (onChange) {
            onChange(newValue);
        }
    };
    // Merge custom styles with default customStyles
    const mergedStyles = React.useMemo(() => {
        const passedStyles = props.styles;
        if (!passedStyles)
            return customStyles;
        return Object.keys(customStyles).reduce((acc, key) => {
            const styleKey = key;
            const defaultStyle = customStyles[styleKey];
            const passedStyle = passedStyles[styleKey];
            if (passedStyle && defaultStyle && typeof defaultStyle === 'function' && typeof passedStyle === 'function') {
                acc[styleKey] = (provided, state) => {
                    const defaultResult = defaultStyle(provided, state);
                    return passedStyle(defaultResult, state);
                };
            }
            else if (passedStyle) {
                acc[styleKey] = passedStyle;
            }
            else {
                acc[styleKey] = defaultStyle;
            }
            return acc;
        }, {});
    }, [customStyles, props.styles]);
    const RowLimitedValueContainer = React.useMemo(() => (maxVisibleRows ? createRowLimitedValueContainer(maxVisibleRows) : undefined), [maxVisibleRows]);
    return (_jsxs("div", { className: cn("space-y-1 text-[13px]", className), children: [label && (_jsxs("label", { className: "block text-[13px] font-medium text-theme-primary", children: [label, required && _jsx("span", { className: "text-red-500 ml-1", children: "*" })] })), _jsx(ReactSelect, { ...props, classNamePrefix: "erp-select", classNames: {
                    control: () => 'text-[13px]',
                    placeholder: () => 'text-[13px]',
                    singleValue: () => 'text-[13px]',
                    input: () => 'text-[13px]',
                    option: () => 'text-[13px]',
                }, styles: mergedStyles, components: {
                    DropdownIndicator,
                    ClearIndicator,
                    MultiValueRemove,
                    Option: CheckboxOption,
                    ...(RowLimitedValueContainer ? { ValueContainer: RowLimitedValueContainer } : {}),
                }, hideSelectedOptions: false, closeMenuOnSelect: !props.isMulti, onChange: handleChange, menuPortalTarget: typeof window !== 'undefined' ? document.body : null, menuPosition: "absolute", menuPlacement: "auto", theme: (theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary: '#3b82f6',
                        primary75: '#60a5fa',
                        primary50: '#93c5fd',
                        primary25: '#dbeafe',
                        danger: '#ef4444',
                        dangerLight: '#fecaca',
                        neutral0: isDarkMode ? '#1f2937' : '#ffffff',
                        neutral5: isDarkMode ? '#374151' : '#f9fafb',
                        neutral10: isDarkMode ? '#4b5563' : '#f3f4f6',
                        neutral20: isDarkMode ? '#6b7280' : '#e5e7eb',
                        neutral30: isDarkMode ? '#9ca3af' : '#d1d5db',
                        neutral40: isDarkMode ? '#d1d5db' : '#9ca3af',
                        neutral50: isDarkMode ? '#e5e7eb' : '#6b7280',
                        neutral60: isDarkMode ? '#f3f4f6' : '#4b5563',
                        neutral70: isDarkMode ? '#f9fafb' : '#374151',
                        neutral80: isDarkMode ? '#ffffff' : '#1f2937',
                        neutral90: isDarkMode ? '#ffffff' : '#111827',
                    },
                }) }), error && (_jsx("p", { className: "text-sm text-red-600 dark:text-red-400", children: error })), helperText && !error && (_jsx("p", { className: "text-sm text-gray-500 dark:text-gray-400", children: helperText }))] }));
}
export default Select;
